/**
 * Canvas Renderer Module
 *
 * This module provides canvas-based rendering functionality for QR codes.
 * It handles drawing QR modules, center overlays, bay labels, and logos.
 *
 * Requirements: 3.2, 3.3, 4.3, 5.4, 6.3, 6.4, 10.2, 10.3
 * - 3.2: Render QR code modules in the selected color
 * - 3.3: Maintain white background for QR code
 * - 4.3: Render white square in center when toggle enabled
 * - 5.4: Render bay number in center overlay for fixed bays
 * - 6.3: Render logo image within center overlay
 * - 6.4: Scale logo to fit within overlay while maintaining aspect ratio
 * - 10.2: Use canvas-based rendering for QR code generation
 * - 10.3: Support rendering QR codes at configurable resolutions
 *
 * Properties validated:
 * - Property 2: Color Application - QR modules rendered in specified color
 * - Property 3: White Background Invariant - Background always white (#FFFFFF)
 * - Property 13: Configurable Resolution Output - Canvas matches requested size
 */

import type { QrMatrix } from './qrGenerator';
import type { FontFamily } from '../types';

/**
 * White color constant for background and center overlay
 * Property 3: White Background Invariant
 */
export const WHITE_BACKGROUND = '#FFFFFF';

/**
 * Creates a new canvas element with the specified size
 *
 * Property 13: Configurable Resolution Output
 * The canvas dimensions will match the requested size exactly.
 *
 * @param size - The width and height of the canvas in pixels
 * @returns A new HTMLCanvasElement with the specified dimensions
 *
 * Requirements: 10.2, 10.3
 */
export function createQrCanvas(size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  return canvas;
}

/**
 * Renders QR code modules onto the canvas context
 *
 * Property 2: Color Application
 * All dark modules (true values in matrix) are rendered in the specified color.
 *
 * Property 3: White Background Invariant
 * The background is filled with white before drawing modules.
 *
 * @param ctx - The canvas 2D rendering context
 * @param matrix - The QR code matrix containing module data
 * @param color - The hex color for dark modules (e.g., '#000000')
 * @param size - The total canvas size in pixels
 *
 * Requirements: 3.2, 3.3, 10.2
 */
export function renderQrModules(
  ctx: CanvasRenderingContext2D,
  matrix: QrMatrix,
  color: string,
  size: number
): void {
  const moduleCount = matrix.size;
  const moduleSize = size / moduleCount;
  const radius = moduleSize * 0.4;
  // Small overlap to eliminate sub-pixel hairline gaps between adjacent modules
  const overlap = 0.5;

  const isDark = (r: number, c: number): boolean =>
    r >= 0 && r < moduleCount && c >= 0 && c < moduleCount && matrix.modules[r][c];

  // Property 3: Fill background with white
  ctx.fillStyle = WHITE_BACKGROUND;
  ctx.fillRect(0, 0, size, size);

  // Property 2: Draw dark modules in specified color with selectively rounded corners.
  // Corners are only rounded when the module has no dark neighbor on that side,
  // so adjacent modules connect seamlessly.
  ctx.fillStyle = color;

  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (!matrix.modules[row][col]) continue;

      // Snap to whole pixels and add slight overlap to prevent hairline gaps
      const x = Math.floor(col * moduleSize);
      const y = Math.floor(row * moduleSize);
      const w = Math.ceil((col + 1) * moduleSize) - x + overlap;
      const h = Math.ceil((row + 1) * moduleSize) - y + overlap;

      const top = isDark(row - 1, col);
      const bottom = isDark(row + 1, col);
      const left = isDark(row, col - 1);
      const right = isDark(row, col + 1);

      // A corner is rounded only when both adjacent sides are free
      const tl = (!top && !left) ? radius : 0;
      const tr = (!top && !right) ? radius : 0;
      const br = (!bottom && !right) ? radius : 0;
      const bl = (!bottom && !left) ? radius : 0;

      ctx.beginPath();
      ctx.roundRect(x, y, w, h, [tl, tr, br, bl]);
      ctx.fill();
    }
  }
}

/**
 * Renders a white center overlay square on the canvas
 *
 * This creates a white square in the center of the QR code where
 * logos or bay labels can be placed. The overlay is centered on the canvas.
 *
 * @param ctx - The canvas 2D rendering context
 * @param size - The total canvas size in pixels
 * @param overlaySize - The size of the center overlay square in pixels
 *
 * Requirements: 4.3
 */
export function renderCenterOverlay(
  ctx: CanvasRenderingContext2D,
  size: number,
  overlaySize: number,
  moduleCount: number
): void {
  if (overlaySize <= 0) {
    return;
  }

  const moduleSize = size / moduleCount;

  // How many modules the overlay spans, rounded up to the nearest ODD number.
  // QR codes have an odd module count, so an odd overlay span guarantees a
  // symmetric center — equal modules on the left and right (and top/bottom).
  const rawModules = Math.ceil(overlaySize / moduleSize);
  const overlayModules = rawModules % 2 === 0 ? rawModules + 1 : rawModules;

  // Place the overlay symmetrically around the center module
  const halfSpan = (overlayModules - 1) / 2;
  const centerModule = (moduleCount - 1) / 2;
  const startModule = centerModule - halfSpan;
  const endModule = startModule + overlayModules;

  // Convert back to pixel coordinates, snapped to module edges.
  // Add a small overlap to cover the sub-pixel anti-alias bleed from
  // adjacent dark modules (renderQrModules adds 0.5px overlap per module).
  const overlap = 1;
  const x = Math.floor(startModule * moduleSize) - overlap;
  const y = Math.floor(startModule * moduleSize) - overlap;
  const w = Math.ceil(endModule * moduleSize) - x + overlap;
  const h = w; // square

  // Draw white square aligned to module grid
  ctx.fillStyle = WHITE_BACKGROUND;
  ctx.fillRect(x, y, w, h);
}

/**
 * Maps FontFamily type to actual CSS font-family string
 *
 * @param fontFamily - The FontFamily type value
 * @returns The CSS font-family string with fallbacks
 */
function getFontFamilyString(fontFamily: FontFamily): string {
  switch (fontFamily) {
    case 'Topgolf':
      return '"Topgolf", sans-serif';
    case 'ShieldSans':
      return '"ShieldSans", sans-serif';
    default:
      // Exhaustive check - TypeScript will error if a case is missing
      const _exhaustive: never = fontFamily;
      return _exhaustive;
  }
}
/**
 * Ensures a font is loaded and ready for canvas rendering.
 *
 * CSS @font-face fonts are loaded lazily by the browser. When a font is first
 * used on a canvas (via ctx.font), the browser may not have fetched the font
 * file yet, causing a silent fallback to the generic family (e.g. sans-serif).
 * Unlike DOM text, canvas text rendering does NOT trigger font loading.
 *
 * This function uses the CSS Font Loading API to explicitly load the font
 * before it's used on canvas, preventing the fallback flash.
 *
 * @param fontFamily - The FontFamily to ensure is loaded
 * @param fontSize - A representative font size (used for the load request)
 */
async function ensureFontLoaded(fontFamily: FontFamily): Promise<void> {
  const fontString = `bold 16px ${getFontFamilyString(fontFamily)}`;
  try {
    await document.fonts.load(fontString);
  } catch {
    // Font loading failed — canvas will fall back to sans-serif.
    // This is non-critical, so we swallow the error silently.
  }
}



/**
 * Reference canvas size used for font size calculations.
 * Font sizes in the template are defined relative to this size.
 * When rendering at different sizes, fonts scale proportionally.
 */
export const REFERENCE_CANVAS_SIZE = 256;

/**
 * Renders a bay label (bay number) in the center overlay
 *
 * The bay number is rendered centered within the overlay area using
 * the specified font family and size. The font size is scaled proportionally
 * based on the canvas size to ensure consistent appearance across different
 * output resolutions (preview vs download).
 *
 * @param ctx - The canvas 2D rendering context
 * @param bayNumber - The bay label text to display
 * @param fontFamily - The font family to use ('Topgolf' or 'ShieldSans')
 * @param fontSize - The base font size (relative to REFERENCE_CANVAS_SIZE)
 * @param overlaySize - The size of the center overlay area
 * @param canvasSize - The total canvas size (needed for centering and scaling)
 *
 * Requirements: 5.4, 10.4
 */
export function renderBayLabel(
  ctx: CanvasRenderingContext2D,
  bayNumber: string,
  fontFamily: FontFamily,
  fontSize: number,
  overlaySize: number,
  canvasSize: number,
  fontColor: string = '#000000'
): void {
  if (overlaySize <= 0) {
    return;
  }

  const text = bayNumber;
  const fontFamilyString = getFontFamilyString(fontFamily);

  // Scale font size proportionally to canvas size
  // This ensures the label appears the same relative size in preview and download
  const scaleFactor = canvasSize / REFERENCE_CANVAS_SIZE;
  const scaledFontSize = Math.round(fontSize * scaleFactor);

  // Set font properties
  ctx.font = `bold ${scaledFontSize}px ${fontFamilyString}`;
  ctx.fillStyle = fontColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Calculate center position using actual font metrics for true optical centering.
  // textBaseline 'middle' uses the font's mathematical midpoint which doesn't account
  // for the visual weight of glyphs, causing a slight vertical offset.
  const centerX = canvasSize / 2;
  const metrics = ctx.measureText(text);
  const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  const centerY = (canvasSize / 2) + (textHeight / 2) - metrics.actualBoundingBoxDescent;

  // Draw the bay number
  ctx.fillText(text, centerX, centerY);
}

/**
 * Calculates scaled dimensions for a logo to fit within the overlay
 * while maintaining aspect ratio
 *
 * Property 7: Logo Scaling Aspect Ratio
 * The scaled dimensions fit within overlay bounds while preserving
 * the original aspect ratio.
 *
 * @param logoWidth - Original logo width
 * @param logoHeight - Original logo height
 * @param maxSize - Maximum size (overlay size)
 * @returns Scaled width and height
 */
function calculateLogoScaledDimensions(
  logoWidth: number,
  logoHeight: number,
  maxSize: number
): { width: number; height: number } {
  if (logoWidth <= 0 || logoHeight <= 0 || maxSize <= 0) {
    return { width: 0, height: 0 };
  }

  const aspectRatio = logoWidth / logoHeight;

  let scaledWidth: number;
  let scaledHeight: number;

  if (aspectRatio >= 1) {
    // Landscape or square - constrain by width
    scaledWidth = Math.min(logoWidth, maxSize);
    scaledHeight = scaledWidth / aspectRatio;
  } else {
    // Portrait - constrain by height
    scaledHeight = Math.min(logoHeight, maxSize);
    scaledWidth = scaledHeight * aspectRatio;
  }

  // Ensure both dimensions fit within maxSize
  if (scaledWidth > maxSize) {
    scaledWidth = maxSize;
    scaledHeight = scaledWidth / aspectRatio;
  }
  if (scaledHeight > maxSize) {
    scaledHeight = maxSize;
    scaledWidth = scaledHeight * aspectRatio;
  }

  return {
    width: Math.round(scaledWidth),
    height: Math.round(scaledHeight),
  };
}

/**
 * Renders a logo image in the center overlay
 *
 * The logo is scaled to fit within the overlay while maintaining
 * its aspect ratio, then centered within the overlay area.
 *
 * Note: This function is async because it needs to load the image
 * from the data URL before rendering.
 *
 * @param ctx - The canvas 2D rendering context
 * @param logoDataUrl - The logo image as a data URL
 * @param overlaySize - The size of the center overlay area
 * @param canvasSize - The total canvas size (needed for centering)
 * @returns Promise that resolves when the logo is rendered
 *
 * Requirements: 6.3, 6.4
 */
export function renderLogo(
  ctx: CanvasRenderingContext2D,
  logoDataUrl: string,
  overlaySize: number,
  canvasSize: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!logoDataUrl || overlaySize <= 0) {
      resolve();
      return;
    }

    const img = new Image();

    img.onload = () => {
      // Calculate scaled dimensions maintaining aspect ratio
      const { width, height } = calculateLogoScaledDimensions(
        img.naturalWidth,
        img.naturalHeight,
        overlaySize
      );

      if (width <= 0 || height <= 0) {
        resolve();
        return;
      }

      // Calculate position to center the logo
      const x = (canvasSize - width) / 2;
      const y = (canvasSize - height) / 2;

      // Draw the logo
      ctx.drawImage(img, x, y, width, height);
      resolve();
    };

    img.onerror = () => {
      reject(new Error('Failed to load logo image'));
    };

    img.src = logoDataUrl;
  });
}

/**
 * Converts a canvas to a data URL (PNG format)
 *
 * @param canvas - The canvas element to convert
 * @returns The canvas content as a PNG data URL
 *
 * Requirements: 7.2, 10.2
 */
export function canvasToDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png');
}

/**
 * Renders a corner label (bay ID/name or site name) in the bottom-right corner
 *
 * The label is rendered right-aligned at the bottom-right of the QR code,
 * inset to the exact corner with no padding. The label covers only the bottom
 * row of QR modules. Uses ShieldSans font with a white background for readability.
 *
 * @param ctx - The canvas 2D rendering context
 * @param text - The text to display (bay ID, bay name, or site name)
 * @param canvasSize - The total canvas size in pixels
 * @param moduleCount - The number of QR modules per side (for calculating module size)
 */
export function renderCornerLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  canvasSize: number,
  moduleCount: number
): void {
  if (!text || canvasSize <= 0 || moduleCount <= 0) {
    return;
  }

  const moduleSize = canvasSize / moduleCount;

  // Font size is approximately 1 module height, with a minimum for readability
  const baseFontSize = Math.max(8, moduleSize * 0.9);
  const fontSize = Math.round(baseFontSize);

  // Set font properties - always use ShieldSans for corner labels
  ctx.font = `${fontSize}px "ShieldSans", sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';

  // Measure text for background
  const textMetrics = ctx.measureText(text);
  const textWidth = textMetrics.width;

  // Fixed right padding (half a module)
  const rightPadding = moduleSize * 0.2;

  // Minimum left padding so text doesn't touch adjacent QR modules
  const minLeftPadding = moduleSize * 0.3;

  // Calculate raw left edge with minimum padding
  const rawLeftEdge = canvasSize - rightPadding - textWidth - minLeftPadding;

  // Snap left edge to nearest module boundary (floor to expand coverage)
  const snappedLeftEdge = Math.floor(rawLeftEdge / moduleSize) * moduleSize;

  // Background width spans from snapped left edge to canvas right edge
  const bgWidth = canvasSize - snappedLeftEdge;

  // Text position: right-aligned with fixed right padding
  const x = canvasSize - rightPadding;
  const y = canvasSize;

  // Draw white background behind text for readability
  // Background height is 1 module plus a small overlap to cover sub-pixel
  // remnants from QR modules rendered with anti-alias overlap above
  // Left edge snaps to module boundary for clean alignment
  const overlapCover = 1;
  ctx.fillStyle = WHITE_BACKGROUND;
  ctx.fillRect(snappedLeftEdge, y - moduleSize - overlapCover, bgWidth, moduleSize + overlapCover * 2);

  // Draw the text in black, right-aligned with fixed right padding
  ctx.fillStyle = '#000000';
  ctx.fillText(text, x, y);
}

/** Ratio of vertical margin above and below the bottom logo relative to QR size */
const BOTTOM_LOGO_VERTICAL_MARGIN_RATIO = 0.03;

/** Ratio of the bottom logo width relative to the QR code size (~70%) */
const BOTTOM_LOGO_WIDTH_RATIO = 0.7;

/** Path to the bottom logo SVG asset */
const BOTTOM_LOGO_PATH = '/tgo-logo.svg';

/**
 * Loads an image from a URL and returns the HTMLImageElement.
 *
 * @param src - The image source URL or data URL
 * @returns Promise resolving to the loaded image element
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Calculates the total canvas height when a bottom logo is included.
 *
 * The extra height accounts for vertical margin above the logo, the logo itself
 * (scaled to ~70% of QR width while preserving aspect ratio), and vertical
 * margin below the logo.
 *
 * @param qrSize - The QR code square size in pixels
 * @param logoAspectRatio - The logo's width / height aspect ratio
 * @returns The total canvas height including the bottom logo area
 */
export function calculateBottomLogoCanvasHeight(
  qrSize: number,
  logoAspectRatio: number
): number {
  const logoWidth = qrSize * BOTTOM_LOGO_WIDTH_RATIO;
  const logoHeight = logoWidth / logoAspectRatio;
  const margin = qrSize * BOTTOM_LOGO_VERTICAL_MARGIN_RATIO;
  return Math.ceil(qrSize + margin + logoHeight + margin);
}

/**
 * Renders the tgo-logo.svg below the QR code area on the canvas.
 *
 * The logo is horizontally centered, spans ~70% of the QR code width,
 * and has vertical margin above and below it.
 *
 * @param ctx - The canvas 2D rendering context
 * @param img - The pre-loaded logo image element
 * @param qrSize - The QR code square size in pixels
 */
function renderBottomLogo(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  qrSize: number
): void {
  const logoWidth = qrSize * BOTTOM_LOGO_WIDTH_RATIO;
  const logoAspectRatio = img.naturalWidth / img.naturalHeight;
  const logoHeight = logoWidth / logoAspectRatio;
  const margin = qrSize * BOTTOM_LOGO_VERTICAL_MARGIN_RATIO;

  const x = (qrSize - logoWidth) / 2;
  const y = qrSize + margin;

  ctx.drawImage(img, x, y, logoWidth, logoHeight);
}

/**
 * Renders a complete QR code with all customizations
 *
 * This is a convenience function that combines all rendering steps:
 * 1. Creates canvas (taller when bottom logo is enabled)
 * 2. Renders QR modules with specified color
 * 3. Optionally renders center overlay
 * 4. Optionally renders logo and/or bay label
 * 5. Optionally renders bottom logo below the QR code
 *
 * @param matrix - The QR code matrix
 * @param size - The QR code square size in pixels
 * @param color - The QR module color (hex format)
 * @param options - Optional center customization options
 * @returns Promise resolving to the canvas data URL
 */
export interface RenderQrOptions {
  readonly centerEnabled?: boolean;
  readonly overlaySize?: number;
  readonly logoDataUrl?: string | null;
  readonly bayNumber?: string;
  readonly fontFamily?: FontFamily;
  readonly fontSize?: number;
  /** Color for the bay label text (hex format, defaults to '#000000') */
  readonly bayLabelColor?: string;
  /** Padding inside the center overlay as a ratio (0-1), reduces logo/label area */
  readonly centerPadding?: number;
  /** Text to display in the bottom-right corner (bay ID/name or site name) */
  readonly cornerLabelText?: string;
  /** Number of QR modules per side (needed for corner label sizing) */
  readonly moduleCount?: number;
  /** Whether to render the tgo-logo.svg below the QR code */
  readonly bottomLogoEnabled?: boolean;
}

export async function renderQrToDataUrl(
  matrix: QrMatrix,
  size: number,
  color: string,
  options: RenderQrOptions = {}
): Promise<string> {
  // Pre-load the bottom logo image if needed so we can calculate canvas height
  let bottomLogoImg: HTMLImageElement | null = null;
  if (options.bottomLogoEnabled) {
    try {
      bottomLogoImg = await loadImage(BOTTOM_LOGO_PATH);
    } catch {
      // If the logo fails to load, continue without it
      bottomLogoImg = null;
    }
  }

  // Determine canvas dimensions — taller when bottom logo is present
  const canvasWidth = size;
  const canvasHeight = bottomLogoImg
    ? calculateBottomLogoCanvasHeight(size, bottomLogoImg.naturalWidth / bottomLogoImg.naturalHeight)
    : size;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  // Fill entire canvas with white (covers bottom logo area too)
  ctx.fillStyle = WHITE_BACKGROUND;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Render QR modules (includes white background for QR area)
  renderQrModules(ctx, matrix, color, size);

  // Render center customizations if enabled
  if (options.centerEnabled && options.overlaySize && options.overlaySize > 0) {
    // Draw white center overlay, snapped to module grid
    renderCenterOverlay(ctx, size, options.overlaySize, matrix.size);

    // Calculate content area with padding applied (for logo only)
    const padding = options.centerPadding ?? 0.15;
    const logoContentSize = options.overlaySize * (1 - padding * 2);

    // Render logo if provided (with padding applied)
    if (options.logoDataUrl) {
      await renderLogo(ctx, options.logoDataUrl, logoContentSize, size);
    }

    // Render bay label if provided (on top of logo if both exist)
    // Bay labels use full overlay size - padding only applies to logo
    if (options.bayNumber !== undefined) {
      await ensureFontLoaded(options.fontFamily ?? 'ShieldSans');
      renderBayLabel(
        ctx,
        options.bayNumber,
        options.fontFamily ?? 'ShieldSans',
        options.fontSize ?? 40,
        options.overlaySize,
        size,
        options.bayLabelColor ?? '#1a9ed5'
      );
    }
  }

  // Render corner label if provided
  if (options.cornerLabelText) {
    await ensureFontLoaded('ShieldSans');
    const moduleCount = options.moduleCount ?? matrix.size;
    renderCornerLabel(ctx, options.cornerLabelText, size, moduleCount);
  }

  // Render bottom logo below the QR code
  if (bottomLogoImg) {
    renderBottomLogo(ctx, bottomLogoImg, size);
  }

  return canvasToDataUrl(canvas);
}
